"use client";

import { useCallback, useMemo } from "react";

import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";

import { PurchaseAuth, PurchaseBuyer } from "@/types/shop";

import { usePatientMe } from "@/api/patient/portal-query";

/**
 * Adapter from the existing MFA OTP session to the surface-agnostic
 * `PurchaseAuth` interface. Used on the public `/{tenant}/shop` route.
 *
 * `ensureAuthenticated` is a noop here — the route's `<MfaGate>` (or the
 * `MfaDialog` component used elsewhere in self-schedule) is what actually
 * triggers OTP. Components that need to gate an action on auth just read
 * `isAuthenticated` and rely on the gate.
 */
export function useMfaPurchaseAuth(): PurchaseAuth {
  const { claimedPatient: patient } = usePatientClaim();
  const patientMe = usePatientMe(patient?.id ?? "", Boolean(patient));

  const buyer = useMemo<PurchaseBuyer | null>(() => {
    if (!patient) return null;
    return {
      id: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName ?? null,
      phoneNumber: patient.phoneNumber ?? null,
      email: patient.email ?? null,
      locationId: patient.locationId ?? null,
      primaryLocationId: patient.primaryLocationId ?? null,
      surchargeDisabled: patient.surchargeDisabled ?? false,
      address: patientMe.data?.address ?? null,
      addressLineTwo: patientMe.data?.addressLineTwo ?? null,
      city: patientMe.data?.city ?? null,
      state: patientMe.data?.state ?? null,
      zipCode: patientMe.data?.zipCode ?? null,
    };
  }, [patient, patientMe.data]);

  const ensureAuthenticated = useCallback(() => {
    // Intentional no-op: the MFA dialog is mounted by the surrounding page;
    // it observes the same session and opens automatically when needed.
  }, []);

  return {
    buyer,
    isAuthenticated: Boolean(patient),
    isBuyerLoading: Boolean(patient) && patientMe.isPending,
    ensureAuthenticated,
  };
}
